import React, {useEffect, useState} from 'react';
import Table from '../../../components/common/table/Table';
import {GetAdminRequestTableResponseData} from "../../../types/admin";
import {Column} from "../../common/table/TableSection";
import DateRangeFilter from '../../common/table/DateRangeFilter';
import {useNavigate} from "react-router-dom";
import {adminRequestTableAPI} from "../../../services/admin";
import {RequestStatus, RequestType} from "../../../types/request";
import styles from "./AdminRequestTable.module.css"

interface DateRange {
    start: Date | null;
    end: Date | null;
}

interface DateRanges {
    create: DateRange;
}

//어드민 요청 테이블
const AdminRequestTable = () => {
    const navigate = useNavigate();
    const [data, setData] = useState<GetAdminRequestTableResponseData[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [filters, setFilters] = useState<Record<string, any>>({
        type: ['가입', '프로젝트', '작업자'],
        create: 'ALL',
        status: ['대기']
    });

    //날짜 필터 값
    const [dateRanges, setDateRanges] = useState<DateRanges>({
        create: {start: null, end: null}
    });

    // 리셋 상태 추가
    const [isResetting, setIsResetting] = useState(false);

    // 필터 전체 초기화
    const resetAllFilters = () => {
        setIsResetting(true);

        // 필터 초기화
        setFilters({
            type: ['가입', '프로젝트', '작업자'],
            create: 'ALL',
            status: ['대기']
        });

        // 날짜 범위 초기화
        setDateRanges({
            create: { start: null, end: null }
        });

        // 페이지 초기화
        setPage(1);

        // 다음 렌더링 사이클에서 리셋 상태 해제
        setTimeout(() => setIsResetting(false), 0);
    };

    //테이블 로우 선택 시
    const handleRowClick = (row: GetAdminRequestTableResponseData) => {
        if (row.requestType === RequestType.가입) {
            //가입 요청, 클라이언트 상세 페이지로 이동
            navigate(`/admin/clients/account/${row.specificId}`);
        }
        if (row.requestType === RequestType.작업자) {
            //작업자 추가 요청, 작업자 추가 요청 상세 페이지로 이동
            navigate(`/admin/billing/worker-request/${row.specificId}`);
        }
        if (row.requestType === RequestType.프로젝트) {
            //프로젝트 추가 요청, 프로젝트 추가 요청 상세 페이지로 이동
            navigate(`/admin/projects/request/${row.specificId}`);
        }
    };

    //컬럼
    const columns: Column<GetAdminRequestTableResponseData>[] = [
        {
            key: 'companyName',
            header: '회사명',
            width: '25%'
        },
        {
            key: 'requestType',
            header: '요청종류',
            width: '25%'
        },
        {
            key: 'createdAt',
            header: '요청일자',
            width: '25%'
        },
        {
            key: 'requestStatus',
            header: '처리상태',
            width: '25%'
        }
    ];

    //요청 종류 필터
    const typeFilterOptions = [
        {label: '가입', value: '가입'},
        {label: '프로젝트', value: '프로젝트'},
        {label: '작업자', value: '작업자'}
    ];

    //요청 처리 상태 필터
    const statusFilterOptions = [
        {label: '대기', value: '대기'},
        {label: '승인', value: '승인'},
        {label: '반려', value: '반려'},
        {label: '추가견적', value: '추가견적'}
    ];

    //API 사용 위한 날짜 데이터 형식 변환
    const formatDateForAPI = (date: Date | string | null): string => {
        if (!date) return '';
        const dateObj = date instanceof Date ? date : new Date(date);

        if (isNaN(dateObj.getTime())) {
            return '-';
        }
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    //API 사용 위한 날짜 범위 데이터 형식 변환
    const formatDateRangeForAPI = (start: Date | null, end: Date | null): string | null => {
        //선택 안한 경우 'ALL'로 표시
        if (!start && !end) return 'ALL';
        if (!end) return `${formatDateForAPI(start)}~`;
        //시작날짜~종료날짜 형식으로 변환
        return `${formatDateForAPI(start)}~${formatDateForAPI(end)}`;
    };

    //날짜 형식 변환
    const handleDateRangeChange = (key: string, start: Date | null, end: Date | null) => {
        // 리셋 중일 때는 날짜 변경 로직 건너뛰기
        if (isResetting) return;

        setDateRanges(prev => ({
            ...prev,
            [key]: {start, end}
        }));

        const dateRangeString = formatDateRangeForAPI(start, end);
        setFilters(prev => ({
            ...prev,
            [key]: dateRangeString
        }));

        setPage(1);
    };

    //테이블 필터 설정
    const tableFilters = [
        {
            key: 'type',
            label: '요청 종류',
            options: typeFilterOptions,
            multiple: true,
            value: filters.type,
            component: undefined
        },
        {
            key: 'create',
            label: '요청일자',
            component: (
                <DateRangeFilter
                    label="요청일자"
                    startDate={dateRanges.create.start}
                    endDate={dateRanges.create.end}
                    onDateChange={(start, end) => handleDateRangeChange('create', start, end)}
                    isOpen={false}
                    onToggle={() => {
                    }}
                />
            ),
            options: [],
            multiple: false,
            value: null
        },
        {
            key: 'status',
            label: '처리 상태',
            options: statusFilterOptions,
            multiple: true,
            value: filters.status,
            component: undefined
        }
    ];

    //데이터 설정
    const fetchData = async () => {
        try {
            setLoading(true);
            //요청 테이블 조회 API
            const response = await adminRequestTableAPI({
                page: page - 1,
                status: filters.status as RequestStatus[],
                create: filters.create,
                type: filters.type as RequestType[]
            });
            //데이터 설정
            setData(response.content);
            //페이지 설정
            setTotalPages(Math.ceil(response.total / 4));
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [page, filters]);

    //필터값 변경
    const handleFilterChange = (key: string, value: any) => {
        setFilters(prev => {
            //요청 처리 상태 필터 변경
            if (key === 'status') {
                const newStatuses = value;
                if (newStatuses.length === 0) {
                    return prev;
                }
                return {...prev, status: newStatuses};
            }
            //요청 종류 필터 변경
            if (key === 'type') {
                const newTypes = value;
                if (newTypes.length === 0) {
                    return {...prev, [key]: ['가입', '프로젝트', '작업자']};
                }
                return {...prev, [key]: newTypes};
            }
            return {...prev, [key]: value};
        });
        setPage(1);
    };

    return (
        <div className={styles.tableContainer}>
            <h3 className={styles.header}>클라이언트 요청</h3>
            <div className={styles.customTableWrapper}>
                <Table
                    columns={columns}
                    data={data}
                    loading={loading}
                    size="medium"
                    filters={tableFilters}
                    showPagination={true}
                    page={page}
                    pageSize={4}
                    totalItems={totalPages * 4}
                    onPageChange={setPage}
                    onFilterChange={handleFilterChange}
                    onRowClick={handleRowClick}
                    onReset={resetAllFilters}
                />
            </div>
        </div>
    );
};

export default AdminRequestTable;